import React, {useCallback, useEffect, useRef, useState} from "react";

import {
    Alert,
    Button,
    Card,
    Checkbox,
    DatePicker,
    Empty,
    Flex,
    message,
    notification,
    Popconfirm,
    Progress,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    DownloadOutlined,
    ReloadOutlined,
    StopOutlined,
    SyncOutlined,
    ThunderboltOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import Apiurl from "../ConstURL";
import AsinReportProgress, {
    CANCEL_PENDING_HINT,
    fmtDuration,
    fmtListingSummary,
    jobDurationSec,
    LISTING_HINT,
    ZORO_FAILED_HINT,
} from "./AsinReportProgress";

dayjs.extend(utc);

const {Text, Title} = Typography;
const {RangePicker} = DatePicker;

// Пока отчёт формируется, опрашиваем часто: на панели живой ход работы. Опросы идут цепочкой
// (следующий — через POLL_MS после ответа на предыдущий), два запроса одновременно не висят.
const POLL_MS = 2000;
const POLL_RETRY_MS = 6000; // после неудачного запроса
// Вкладка в фоне: редкий опрос, только чтобы заголовок вкладки показал «готово». 0 — полная пауза.
const HIDDEN_POLL_MS = 30000;
const LOAD_TIMEOUT_MS = 15000;

const FILE_GONE_HINT = "файл удалён — хранятся 10 последних отчётов за 30 дней";

const isZeroTime = (iso) => !iso || iso.startsWith("0001");
// Даты периода и диапазона данных — календарные дни: берём дату из строки, без сдвига часового пояса.
const fmtDay = (iso) => (isZeroTime(iso) ? "—" : `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`);
const fmtMsk = (iso) => (isZeroTime(iso) ? "" : dayjs(iso).utcOffset(180).format("DD.MM.YYYY HH:mm") + " МСК");

const intFmt = new Intl.NumberFormat("ru-RU");
const moneyFmt = new Intl.NumberFormat("ru-RU", {minimumFractionDigits: 2, maximumFractionDigits: 2});
const fmtInt = (v) => intFmt.format(Number(v) || 0);
const fmtMoney = (v) => moneyFmt.format(Number(v) || 0);

const fmtPeriod = (from, to) => {
    if (from && to) return `${fmtDay(from)} – ${fmtDay(to)}`;
    if (to) return `по ${fmtDay(to)}`;
    if (from) return `с ${fmtDay(from)}`;
    return "за всё время";
};

const isActive = (j) => j.Status === "queued" || j.Status === "running";

// Задача для живой панели: самая свежая из идущих, иначе самая свежая из ожидающих в очереди.
const pickPanelJob = (list) => {
    const newest = (status) => list.reduce((best, j) => {
        if (j.Status !== status) return best;
        if (!best) return j;
        return (Date.parse(j.CreatedAt) || 0) > (Date.parse(best.CreatedAt) || 0) ? j : best;
    }, null);
    return newest("running") || newest("queued");
};

const downloadHref = (r) =>
    Apiurl + "/asinreport/file?" + new URLSearchParams({token: localStorage.getItem("token"), id: r.Id, v: r.FinishedAt || ""});

const STATUS = {
    queued: {color: "default", label: "в очереди", icon: <ClockCircleOutlined/>},
    running: {color: "processing", label: "формируется", icon: <SyncOutlined spin/>},
    done: {color: "green", label: "готов", icon: <CheckCircleOutlined/>},
    failed: {color: "red", label: "ошибка", icon: <CloseCircleOutlined/>},
    cancelled: {color: "default", label: "отменён", icon: <StopOutlined/>},
};

// Ключ галочки -> поле Config в ответе сервера.
const OPTIONS = [
    {key: "listings", cfg: "Listings", label: "Статус в листингах"},
    {key: "zoro", cfg: "Zoro", label: "Данные Zoro"},
    {key: "warehouse", cfg: "Warehouse", label: "Остаток на складе"},
];

const rangePresets = [
    {label: "За всё время", value: [null, null]},
    {label: "С начала года", value: () => [dayjs().startOf("year"), dayjs()]},
    {label: "Последние 12 месяцев", value: () => [dayjs().subtract(12, "month"), dayjs()]},
    {label: "Последние 90 дней", value: () => [dayjs().subtract(89, "day"), dayjs()]},
];

const cardStyle = {width: "100%", maxWidth: 1100, borderRadius: 16, border: "1px solid #2a2140"};
const wrapStyle = {width: "100%", boxSizing: "border-box", padding: "8px 28px 24px"};

export default function AsinSalesReport() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const loadSeq = useRef(0);
    const [loadError, setLoadError] = useState(false);

    const [range, setRange] = useState([null, null]);
    const [opts, setOpts] = useState({listings: true, zoro: true, warehouse: true, zorofresh: false});
    const [submitting, setSubmitting] = useState(false);
    const [cancelling, setCancelling] = useState("");

    // Смещение часов сервера относительно браузера (мс): таймеры панели считаем по времени сервера.
    const [serverOffset, setServerOffset] = useState(0);
    const offsetRef = useRef(null);

    // Опрос: таймер следующего запроса, нужен ли опрос (есть задача в работе), текущий запрос.
    const pollTimer = useRef(0);
    const pollWanted = useRef(false);
    const abortRef = useRef(null);
    const loadRef = useRef(null);

    // Задача, за которой следит живая панель, и итог задачи, закончившейся у нас на глазах.
    const [watchId, setWatchId] = useState("");
    const [seenData, setSeenData] = useState(null);
    const [finished, setFinished] = useState(null);
    const notifiedId = useRef("");
    const cancelledByMe = useRef(new Set());
    // Отмена запрошена, а задача ещё идёт (останавливается после текущего шага): id -> статус на момент отмены.
    const [cancelPending, setCancelPending] = useState({});
    const [notifyApi, notifyHolder] = notification.useNotification();
    const [msgApi, msgHolder] = message.useMessage();

    const scheduleNext = useCallback((failed = false) => {
        clearTimeout(pollTimer.current);
        pollTimer.current = 0;
        if (!pollWanted.current) return;
        let delay = failed ? POLL_RETRY_MS : POLL_MS;
        if (document.visibilityState === "hidden") delay = HIDDEN_POLL_MS;
        if (delay > 0) pollTimer.current = setTimeout(() => loadRef.current?.(true), delay);
    }, []);

    // silent — фоновое обновление при опросе: без индикатора загрузки в таблице.
    const load = useCallback((silent = false) => {
        clearTimeout(pollTimer.current);
        pollTimer.current = 0;
        // Новый запрос заменяет недождавшийся старый — одновременно висит не больше одного.
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        // Ответы могут прийти не по порядку — применяем только последний запрос.
        const seq = ++loadSeq.current;
        const token = localStorage.getItem("token");
        if (!silent) setLoading(true);
        const sentAt = Date.now();
        let failed = false;
        axios.get(Apiurl + "/asinreport/jobs?" + new URLSearchParams({token}), {timeout: LOAD_TIMEOUT_MS, signal: ctrl.signal})
            .then((res) => {
                if (seq !== loadSeq.current) return;
                pollWanted.current = !!res.data?.Running || (res.data?.Jobs || []).some(isActive);
                const serverMs = Date.parse(res.data?.ServerTime || "");
                if (!Number.isNaN(serverMs)) {
                    const offset = serverMs - (sentAt + Date.now()) / 2;
                    // Мелкие колебания (задержка сети) игнорируем, чтобы таймеры не дёргались.
                    if (offsetRef.current === null || Math.abs(offset - offsetRef.current) > 1000) {
                        offsetRef.current = offset;
                        setServerOffset(offset);
                    }
                }
                setData(res.data);
                setLoadError(false);
            })
            .catch((err) => {
                if (seq !== loadSeq.current || axios.isCancel?.(err)) return;
                failed = true;
                setLoadError(true);
                if (err?.response?.status === 404) {
                    // 404 бывает и при истёкшей сессии, и если сервер ещё без этого раздела — проверяем сессию.
                    axios.get(Apiurl + "/user/auth/check?" + new URLSearchParams({token})).catch((e) => {
                        if (e?.response?.status === 404) {
                            localStorage.setItem("token", "");
                            window.location.replace("/login");
                        }
                    });
                }
            })
            .finally(() => {
                // Следующий опрос планирует только последний запрос.
                if (seq !== loadSeq.current) return;
                setLoading(false);
                scheduleNext(failed);
            });
    }, [scheduleNext]);

    useEffect(() => {
        loadRef.current = load;
        load();
        return () => {
            // Уход со страницы: останавливаем опрос и игнорируем запоздавшие ответы.
            loadRef.current = null;
            loadSeq.current += 1;
            clearTimeout(pollTimer.current);
            pollTimer.current = 0;
            abortRef.current?.abort();
        };
    }, [load]);

    // Вкладка вернулась на передний план — сразу обновляем; ушла в фон — опрос переходит на редкий.
    useEffect(() => {
        const onVisibility = () => {
            if (document.visibilityState === "visible") loadRef.current?.(true);
            else if (pollTimer.current) scheduleNext();
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, [scheduleNext]);

    const jobs = data?.Jobs || [];
    const running = !!data?.Running || jobs.some(isActive);

    // Пришли новые данные. Задача панели закончилась между опросами — переключаем панель на итог
    // прямо в этом рендере (без промежуточного кадра, где панели нет).
    if (data !== seenData) {
        setSeenData(data);
        if (data) {
            const current = pickPanelJob(jobs);
            if (watchId && (!current || current.Id !== watchId)) {
                const job = jobs.find((j) => j.Id === watchId);
                if (job && !isActive(job)) setFinished(job);
            }
            const nextId = current ? current.Id : "";
            if (nextId !== watchId) setWatchId(nextId);

            // «Отмена…» держим, пока статус задачи не изменился.
            const pendingIds = Object.keys(cancelPending);
            const keep = pendingIds.filter((id) => jobs.find((j) => j.Id === id)?.Status === cancelPending[id]);
            if (keep.length !== pendingIds.length) {
                setCancelPending(Object.fromEntries(keep.map((id) => [id, cancelPending[id]])));
            }
        }
    }

    // Уведомление об окончании задачи, за которой следила панель.
    useEffect(() => {
        const job = finished;
        if (!job || notifiedId.current === job.Id) return;
        notifiedId.current = job.Id;
        const key = "asinreport-" + job.Id;
        const dur = jobDurationSec(job);
        if (job.Status === "done") {
            notifyApi.success({
                key,
                message: "Отчёт «Все ASIN с продажами» готов",
                description: `Период: ${fmtPeriod(job.From, job.To)}.` + (dur !== null ? ` Сформирован за ${fmtDuration(dur)}.` : ""),
                btn: job.HasFile ? (
                    <Button type="primary" size="small" icon={<DownloadOutlined/>}
                            href={downloadHref(job)} target="_blank" rel="noreferrer">
                        Скачать Excel
                    </Button>
                ) : undefined,
                duration: 12,
                placement: "bottomRight",
            });
        } else if (job.Status === "failed") {
            notifyApi.error({
                key,
                message: "Отчёт не сформирован",
                description: job.Message || "Ошибка при формировании отчёта",
                duration: 12,
                placement: "bottomRight",
            });
        } else if (job.Status === "cancelled" && !cancelledByMe.current.has(job.Id)) {
            notifyApi.info({
                key,
                message: "Формирование отчёта отменено",
                description: job.Message || undefined,
                duration: 8,
                placement: "bottomRight",
            });
        }
    }, [finished, notifyApi]);

    const config = data?.Config;
    const available = (key) => {
        const opt = OPTIONS.find((o) => o.key === key);
        return !(opt && config && config[opt.cfg] === false);
    };
    const effective = {
        listings: opts.listings && available("listings"),
        zoro: opts.zoro && available("zoro"),
        warehouse: opts.warehouse && available("warehouse"),
    };
    effective.zorofresh = opts.zorofresh && effective.zoro;

    const submit = () => {
        const params = {token: localStorage.getItem("token")};
        if (range?.[0]) params.from = range[0].format("YYYY-MM-DD");
        if (range?.[1]) params.to = range[1].format("YYYY-MM-DD");
        ["listings", "zoro", "warehouse", "zorofresh"].forEach((k) => (params[k] = effective[k] ? "1" : "0"));

        setSubmitting(true);
        axios.post(Apiurl + "/asinreport/jobs?" + new URLSearchParams(params))
            .then((res) => {
                if (res.data?.Status) {
                    const job = res.data.Job;
                    if (job) {
                        setData((d) => (d ? {...d, Running: true, Jobs: [job, ...(d.Jobs || []).filter((j) => j.Id !== job.Id)]} : d));
                    }
                    setFinished(null);
                    pollWanted.current = true;
                    msgApi.success("Отчёт поставлен в очередь — ход формирования виден на панели вверху.", 6);
                    load(true);
                } else {
                    msgApi.warning(res.data?.Message || "Не удалось запустить формирование отчёта", 8);
                }
            })
            .catch((err) => {
                const msg = err?.response?.data?.Message;
                if (err?.response?.status === 409) {
                    msgApi.warning(msg || "Отчёт уже формируется — дождитесь окончания", 8);
                    load(true);
                } else {
                    msgApi.error(msg || "Не удалось запустить формирование отчёта", 10);
                }
            })
            .finally(() => setSubmitting(false));
    };

    const cancel = (id) => {
        cancelledByMe.current.add(id);
        setCancelling(id);
        axios.post(Apiurl + "/asinreport/jobs/cancel?" + new URLSearchParams({token: localStorage.getItem("token"), id}))
            .then((res) => {
                if (res.data?.Status) {
                    msgApi.success(res.data.Message || "Формирование отчёта отменено", 6);
                    // Задача останавливается после текущего шага — до смены статуса показываем «отмена…».
                    const job = (data?.Jobs || []).find((j) => j.Id === id);
                    if (job && isActive(job)) setCancelPending((p) => ({...p, [id]: job.Status}));
                } else {
                    msgApi.warning(res.data?.Message || "Не удалось отменить отчёт", 8);
                }
                load(true);
            })
            .catch((err) => msgApi.error(err?.response?.data?.Message || "Не удалось отменить отчёт", 10))
            .finally(() => setCancelling(""));
    };

    const renderStatus = (r) => {
        const st = STATUS[r.Status] || {color: "default", label: r.Status || "—"};
        const tag = <Tag color={st.color} icon={st.icon} style={{marginInlineEnd: 0}}>{st.label}</Tag>;

        if (r.Status === "running" || r.Status === "queued") {
            const total = Number(r.Progress?.Total) || 0;
            const done = Number(r.Progress?.Done) || 0;
            return (
                <Flex vertical gap="4px" style={{minWidth: 190}}>
                    <Flex gap="6px" align="center" wrap="wrap">
                        {tag}
                        {r.StageLabel && <Text type="secondary" style={{fontSize: 12}}>{r.StageLabel}</Text>}
                    </Flex>
                    {r.Status === "running" && total > 0 && (
                        <Tooltip title={`${fmtInt(done)} из ${fmtInt(total)}`}>
                            <div>
                                <Progress
                                    size="small"
                                    status="active"
                                    style={{margin: 0}}
                                    percent={Math.min(100, Math.floor((done / total) * 100))}
                                />
                            </div>
                        </Tooltip>
                    )}
                </Flex>
            );
        }
        if (r.Status === "done") {
            return fmtMsk(r.FinishedAt) ? <Tooltip title={`Готов: ${fmtMsk(r.FinishedAt)}`}>{tag}</Tooltip> : tag;
        }
        return r.Message ? <Tooltip overlayStyle={{maxWidth: 560}} title={r.Message}>{tag}</Tooltip> : tag;
    };

    const renderTotals = (r) => {
        if (r.Status !== "done") return <Text type="secondary">—</Text>;
        const s = r.Stats || {};
        return (
            <Flex vertical gap="6px">
                <Text style={{whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums"}}>
                    ASIN: <b>{fmtInt(s.Asins)}</b> · шт: <b>{fmtInt(s.Units)}</b> · $: <b>{fmtMoney(s.Amount)}</b>
                </Text>
                <Flex gap="4px" wrap="wrap">
                    {r.WithZoro && (
                        <Tooltip
                            overlayStyle={{maxWidth: 420}}
                            title={
                                <>
                                    {s.ZoroCached ? <div>Из кэша: {fmtInt(s.ZoroCached)}</div> : null}
                                    <div>{ZORO_FAILED_HINT}</div>
                                </>
                            }
                        >
                            <Tag color={s.ZoroFailed > 0 ? "orange" : undefined}>
                                Zoro: найдено {fmtInt(s.ZoroFound)} / не найдено {fmtInt(s.ZoroNotFound)} / сбой {fmtInt(s.ZoroFailed)}
                            </Tag>
                        </Tooltip>
                    )}
                    {r.WithWarehouse && (
                        <Tooltip title={`Найдено на складе: ${fmtInt(s.WarehouseMatched)}, из них в наличии: ${fmtInt(s.WarehouseInStock)}`}>
                            <Tag>склад: {fmtInt(s.WarehouseMatched)} ASIN</Tag>
                        </Tooltip>
                    )}
                    {r.WithListings && (
                        <Tooltip overlayStyle={{maxWidth: 420}} title={LISTING_HINT}>
                            <Tag color={s.NotListed > 0 ? "volcano" : undefined}>{fmtListingSummary(s)}</Tag>
                        </Tooltip>
                    )}
                </Flex>
            </Flex>
        );
    };

    const columns = [
        {
            title: "Создан",
            dataIndex: "CreatedAt",
            render: (v, r) => (
                <Flex vertical gap="2px">
                    <Text strong style={{whiteSpace: "nowrap"}}>{fmtMsk(v) || "—"}</Text>
                    {r.CreatedBy && <Text type="secondary" style={{fontSize: 12}}>{r.CreatedBy}</Text>}
                </Flex>
            ),
        },
        {
            title: "Период",
            render: (_, r) => <span style={{whiteSpace: "nowrap"}}>{fmtPeriod(r.From, r.To)}</span>,
        },
        {
            title: "Статус",
            dataIndex: "Status",
            render: (_, r) => renderStatus(r),
        },
        {
            title: "Итоги",
            render: (_, r) => renderTotals(r),
        },
        {
            title: "Предупреждения",
            dataIndex: "Warnings",
            render: (w) =>
                w && w.length > 0 ? (
                    <Tooltip
                        overlayStyle={{maxWidth: 560}}
                        title={w.map((x, i) => <div key={i} style={{marginBottom: 6}}>• {x}</div>)}
                    >
                        <Tag color="orange" icon={<WarningOutlined/>}>{w.length}</Tag>
                    </Tooltip>
                ) : (
                    <Text type="secondary">нет</Text>
                ),
        },
        {
            title: "",
            align: "right",
            render: (_, r) => {
                const pendingCancel = isActive(r) && !!cancelPending[r.Id];
                return (
                    <Flex gap="6px" justify="flex-end" align="center" wrap="wrap">
                        {r.HasFile && (
                            <Tooltip title={r.Filename || undefined}>
                                <Button icon={<DownloadOutlined/>} href={downloadHref(r)} target="_blank" rel="noreferrer">
                                    Excel
                                </Button>
                            </Tooltip>
                        )}
                        {r.Status === "done" && !r.HasFile && (
                            <Text type="secondary" style={{fontSize: 12, maxWidth: 220, textAlign: "right"}}>
                                {FILE_GONE_HINT}
                            </Text>
                        )}
                        {pendingCancel && (
                            <Tag icon={<SyncOutlined spin/>} color="warning" style={{marginInlineEnd: 0}}>отмена…</Tag>
                        )}
                        {pendingCancel && (
                            <Tooltip title={CANCEL_PENDING_HINT}>
                                <Button danger icon={<StopOutlined/>} disabled>Отменить</Button>
                            </Tooltip>
                        )}
                        {isActive(r) && !pendingCancel && (
                            <Popconfirm
                                title="Отменить формирование отчёта?"
                                okText="Отменить отчёт"
                                okButtonProps={{danger: true}}
                                cancelText="Нет"
                                onConfirm={() => cancel(r.Id)}
                            >
                                <Button danger icon={<StopOutlined/>} loading={cancelling === r.Id}
                                        disabled={cancelling !== "" && cancelling !== r.Id}>
                                    Отменить
                                </Button>
                            </Popconfirm>
                        )}
                    </Flex>
                );
            },
        },
    ];

    const activeJob = pickPanelJob(jobs);
    const finishedJob = finished ? jobs.find((j) => j.Id === finished.Id) || finished : null;
    const panelJob = activeJob || (finishedJob && !isActive(finishedJob) ? finishedJob : null);

    if (loadError && !data) {
        return (
            <div style={wrapStyle}>
                <Flex vertical align="center">
                    <Alert
                        type="error"
                        showIcon
                        style={cardStyle}
                        message="Не удалось загрузить данные отчёта «Все ASIN с продажами»"
                        action={<Button onClick={() => load()} loading={loading}>Повторить</Button>}
                    />
                </Flex>
            </div>
        );
    }

    const dataRange = data?.DataRange;
    const rangeHint = [
        dataRange && !isZeroTime(dataRange.Min) && !isZeroTime(dataRange.Max)
            ? `Продажи в базе: с ${fmtDay(dataRange.Min)} по ${fmtDay(dataRange.Max)}.`
            : "",
        "Без дат — за всё время; только «по» — все продажи по эту дату; только «с» — с этой даты до последних данных.",
    ].filter(Boolean).join(" ");

    return (
        <div style={wrapStyle}>
            {notifyHolder}
            {msgHolder}
            <Flex vertical align="center" gap="24px">
                {panelJob && (
                    <AsinReportProgress
                        job={panelJob}
                        typical={data?.Typical}
                        serverOffset={serverOffset}
                        periodLabel={fmtPeriod(panelJob.From, panelJob.To)}
                        downloadHref={downloadHref}
                        onCancel={cancel}
                        cancelling={cancelling}
                        cancelPending={!!cancelPending[panelJob.Id]}
                        onDismiss={() => setFinished(null)}
                    />
                )}

                <Card style={cardStyle} styles={{body: {padding: 28}}}>
                    <Flex vertical gap="20px">
                        <Flex vertical gap="6px" style={{maxWidth: 760}}>
                            <Title level={4} style={{margin: 0}}>Все ASIN с продажами</Title>
                            <Text type="secondary">
                                Все уникальные ASIN с продажами за выбранный период (по дате заказа): основной SKU,
                                Zoro SKU, магазины, продано штук, заказов, сумма $. Дополнительно: выложен ли товар
                                сейчас (по последним All List из manager.enjoytool.net), данные Zoro (наличие, срок отгрузки,
                                название, бренд) и остаток на нашем складе. Отчёт формируется в фоне несколько минут.
                            </Text>
                        </Flex>

                        <Flex vertical gap="6px">
                            <Flex gap="2px 10px" align="baseline" wrap="wrap">
                                <Text strong>Период (по дате заказа)</Text>
                                <Text type="secondary" style={{fontSize: 12}}>дата заказа по UTC, как в отчёте Amazon</Text>
                            </Flex>
                            <RangePicker
                                style={{maxWidth: 340}}
                                format="DD.MM.YYYY"
                                allowEmpty={[true, true]}
                                placeholder={["Дата с", "Дата по"]}
                                presets={rangePresets}
                                value={range}
                                onChange={(v) => setRange(v || [null, null])}
                            />
                            <Text type="secondary" style={{fontSize: 12}}>{rangeHint}</Text>
                        </Flex>

                        <Flex vertical gap="8px">
                            <Text strong>Дополнительные данные</Text>
                            <Flex gap="8px 24px" wrap="wrap" align="center">
                                {OPTIONS.map((o) => (
                                    <Tooltip key={o.key} title={available(o.key) ? undefined : "Не настроено на сервере"}>
                                        <span>
                                            <Checkbox
                                                checked={effective[o.key]}
                                                disabled={!available(o.key)}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    // Без Zoro сбрасываем и принудительное обновление, чтобы оно не включилось само.
                                                    setOpts((p) => ({
                                                        ...p,
                                                        [o.key]: checked,
                                                        ...(o.key === "zoro" && !checked ? {zorofresh: false} : {}),
                                                    }));
                                                }}
                                            >
                                                {o.label}
                                            </Checkbox>
                                        </span>
                                    </Tooltip>
                                ))}
                                <Checkbox
                                    checked={effective.zorofresh}
                                    disabled={!effective.zoro}
                                    onChange={(e) => setOpts((p) => ({...p, zorofresh: e.target.checked}))}
                                >
                                    Обновить данные Zoro принудительно (без кэша 6 ч)
                                </Checkbox>
                            </Flex>
                        </Flex>

                        <Flex gap="12px" align="center" wrap="wrap">
                            <Button type="primary" icon={<ThunderboltOutlined/>} onClick={submit}
                                    loading={submitting} disabled={running || !data}>
                                Сформировать отчёт
                            </Button>
                            {running && (
                                <Text type="secondary">
                                    Отчёт уже формируется — ход работы на панели вверху; дождитесь окончания или отмените его.
                                </Text>
                            )}
                        </Flex>
                    </Flex>
                </Card>

                <Card
                    style={cardStyle}
                    styles={{body: {padding: 24}}}
                    title="Сформированные отчёты"
                    extra={
                        <Flex gap="8px" align="center">
                            {running && <Tag icon={<SyncOutlined spin/>} color="processing">автообновление</Tag>}
                            <Button icon={<ReloadOutlined/>} onClick={() => load()} loading={loading}>Обновить</Button>
                        </Flex>
                    }
                >
                    {loadError && (
                        <Alert
                            type="error"
                            showIcon
                            style={{marginBottom: 12}}
                            message="Не удалось обновить данные — показаны последние загруженные"
                            action={<Button size="small" onClick={() => load()}>Повторить</Button>}
                        />
                    )}
                    <Table
                        rowKey="Id"
                        size="middle"
                        loading={loading}
                        columns={columns}
                        dataSource={jobs}
                        pagination={{pageSize: 15, hideOnSinglePage: true}}
                        locale={{emptyText: <Empty description="Пока нет сформированных отчётов"/>}}
                        scroll={{x: true}}
                    />
                </Card>
            </Flex>
        </div>
    );
}
